/**
 * 概览页：身份、余额、累计指标、最近流水、邀请摘要。
 * 详细流水与邀请记录分别在各自页面，这里只做入口。
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Loader2, Users } from 'lucide-react';
import CheckInButton from '../../components/CheckInButton';
import {
  api,
  invitationAPI,
  type CreditInfo,
  type InvitationInfo,
} from '../../services/api';
import { Metric, SectionCard, TransactionRow, EmptyState, UserAvatar } from './shared';

export default function OverviewPage() {
  const navigate = useNavigate();
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [user, setUser] = useState<{ email: string; name: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadCredits = async () => {
    try {
      const res = await api.getCredits();
      setCreditInfo(res);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载积分信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = api.getSavedUser();
    if (saved) {
      setUser({ email: saved.email, name: saved.name });
    }
    void loadCredits();
    invitationAPI
      .getMyInvitations()
      .then(setInvitation)
      .catch(() => setInvitation(null));
  }, []);

  const stats = creditInfo?.stats;
  const recent = (creditInfo?.transactions ?? []).slice(0, 5);
  const rebate = invitation?.rebate;
  const showInvite = !!invitation && (invitation.inviteEnabled || !!rebate?.enabled);
  const rebateNet = rebate ? rebate.totalCredits - rebate.totalReversed : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <section className="bg-white border border-light-border rounded-2xl shadow-sm p-5 lg:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatar name={user?.name} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-light-text truncate">
                {user?.name || '用户'}
              </p>
              <p className="text-xs text-light-muted truncate">{user?.email}</p>
            </div>
          </div>
          <CheckInButton compact onCheckIn={() => void loadCredits()} />
        </div>

        <div className="mt-6">
          <p className="text-xs text-light-muted">当前积分</p>
          <p className="text-4xl sm:text-5xl font-bold text-light-text mt-1 tabular-nums">
            {creditInfo?.credits ?? 0}
          </p>
        </div>

        <div className="mt-6 pt-5 border-t border-light-border grid grid-cols-3 gap-3">
          <Metric label="累计获得" value={`+${stats?.totalEarned ?? 0}`} tone="earn" />
          <Metric label="累计消费" value={`-${stats?.totalSpent ?? 0}`} tone="spend" />
          <Metric label="本月消费" value={`-${stats?.monthSpent ?? 0}`} />
        </div>
      </section>

      <SectionCard
        title="最近积分变动"
        action={
          <button
            onClick={() => navigate('/user/credits')}
            className="flex items-center gap-1 text-xs text-accent-blue hover:underline"
          >
            查看全部
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        }
      >
        {recent.length === 0 ? (
          <EmptyState text="暂无积分记录" />
        ) : (
          <div className="divide-y divide-light-border">
            {recent.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} />
            ))}
          </div>
        )}
      </SectionCard>

      {showInvite && (
        <SectionCard
          title="邀请推广"
          subtitle="好友注册与充值都会给你带来积分"
          action={
            <button
              onClick={() => navigate('/user/invite')}
              className="flex items-center gap-1 text-xs text-accent-blue hover:underline"
            >
              去邀请
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          }
          bodyClassName="p-5"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-light-hover flex items-center justify-center">
                <Users className="w-4 h-4 text-light-secondary" />
              </div>
              <div>
                <p className="text-xs text-light-muted">已邀请</p>
                <p className="text-lg font-semibold text-light-text tabular-nums">
                  {invitation?.totalInvited ?? 0} 人
                </p>
              </div>
            </div>
            {rebate?.enabled && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-light-hover flex items-center justify-center">
                  <Users className="w-4 h-4 text-light-secondary" />
                </div>
                <div>
                  <p className="text-xs text-light-muted">累计返利</p>
                  <p className="text-lg font-semibold text-green-600 tabular-nums">
                    +{rebateNet}
                  </p>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}
    </>
  );
}
